import { Icon } from '@openeducation/oe-rn-components';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Animated,
  Falsy,
  KeyboardAvoidingView,
  Platform,
  View,
} from 'react-native';
import HTML, { HTMLNode } from 'react-native-render-html';

import styles from './dib-activity.style';
import { DibInput } from './dib-input/dib-input';
import { DibStoreProvider, useDibContext } from './use-dib-store';
import { TemplateType } from '../../../../enums/template-type';
import { useColors } from '../../../../hooks';
import { useActivityContext } from '../../../../hooks/use-activity.context';
import { useErrorSound } from '../../../../hooks/use-error-sound';
import { useSuccessSound } from '../../../../hooks/use-success-sound';
import useVibration from '../../../../hooks/use-vibration';
import { HintButton, HintButtonType } from '../../../hint-button/hint-button';
import KeyboardLegend, {
  KeyboardLegendPadding,
} from '../../../keyboard-legend/keyboard-legend';
import LessonAccentBanner from '../../../lesson-accent-banner/lesson-accent-banner';
import LessonImage from '../../../lesson-image/lesson-image';
import LessonInstructions from '../../../lesson-instructions/lesson-instructions';
import LessonPlayerFrame from '../../../lesson-player-frame/lesson-player-frame';
import { StickAudioPlayer } from '../../../stick-audio-player/stick-audio-player';
import ThemedText2 from '../../../themed-text-2';
import { ThemedView } from '../../../themed-view';
import { Card } from '../../card/card';

enum Type {
  DIB = 'DIB',
  DIB_EXTENDED = 'DIB - EXTENDED',
  TIB = 'TIB',
  TIB_EXTENDED = 'TIB - EXTENDED',
}

interface DibActivityInterface {
  type: Type;
  instructions: string;
  instructionsTranslation?: string;
  text: string;
  image: string;
  audio?: string;
  correctFeedback: string;
  correctFeedbackTranslation?: string;
  incorrectFeedback: string;
  incorrectFeedbackTranslation?: string;
}

const splitBySpaces = (text: string): string[] => text.split(/(?= )/g);

// FIX: This actually returns HMTLNode[] | null but the type is wrong in react-native-render-html
const HTMLAlterChildren = (node: HTMLNode): HTMLNode[] | Falsy => {
  if (node.type !== 'tag') {
    return false;
  }

  const newChildren: HTMLNode[] = [];
  const { children } = node;

  children.forEach((child) => {
    if (child.type === 'text') {
      const dataArr = splitBySpaces(child.data);
      dataArr.forEach((splitData, index) => {
        let data = splitData;
        const nextData = dataArr[index + 1];
        if (nextData === ' ') {
          data += ' ';
        }
        newChildren.push({ ...child, data });
      });
    } else {
      newChildren.push({ ...child });
    }
  });

  // FIX: temp type fix (any) for a problem in the react-native-render-html lib
  return newChildren as any;
};

const HTMLTagStyles = {
  p: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
};

const HTMLFontStyle = {
  fontSize: 20,
  lineHeight: Platform.select({
    web: 46,
    default: 34,
  }),
  fontWeight: 'normal',
} as const;

const HTMLRenderers = (type: 'select' | 'text') => ({
  custom: (htmlAttribs, _children, _convertedCSSStyles, passProps) => {
    const { key } = passProps;
    const { answer, index } = htmlAttribs;

    return (
      <DibInput correctAnswer={answer} key={key} index={index} type={type} />
    );
  },
});

function DibActivity() {
  const colors = useColors();
  const {
    currentActivity,
    completeCurrentActivity,
    isCurrentActivityComplete,
  } = useActivityContext();

  const {
    store: { userAnswers, complete, possibleAnswers, areErrorsVisible },
    actions: {
      setPossibleAnswers,
      reset,
      setErrorsVisible,
      resetIncorrectAnswers,
      markAsComplete,
      fillAnswers,
    },
  } = useDibContext();

  const { t } = useTranslation();
  const correctSound = useSuccessSound();
  const incorrectSound = useErrorSound();
  const activity = currentActivity as DibActivityInterface;
  const { vibrate } = useVibration();
  const [hintButtonType, setHintButtonType] = useState<HintButtonType>('hint');
  const [showHintButton, setShowHintButton] = useState(false);
  const scrollY = useRef(new Animated.Value(0)).current;

  const hasAnsweredSomething = useMemo(
    () => userAnswers.some((answer) => !!answer.answer),
    [userAnswers]
  );

  const type =
    activity.type === Type.TIB || activity.type === Type.TIB_EXTENDED
      ? Type.TIB
      : Type.DIB;

  const areAllAnswersCorrect = useMemo(() => {
    return userAnswers.every(({ correct }) => correct);
  }, [userAnswers]);

  const completeActivity = useCallback(() => {
    markAsComplete();
    completeCurrentActivity();
  }, [markAsComplete, completeCurrentActivity]);

  const hasAnsweredAll = useMemo(() => {
    return userAnswers.every(({ answer }) => answer !== undefined);
  }, [userAnswers]);

  const text = useMemo(() => {
    const replaceAnswersWithCustomTags = (text: string) => {
      const amountOfAnswers = text.match(/\[\[(.*?)\]\]/g);
      let modifiedText = text;
      amountOfAnswers?.forEach((_, index) => {
        modifiedText = modifiedText.replace(
          /\[\[(.*?)\]\]/,
          `<custom answer="$1" index="${index}"></custom>`
        );
      });

      return modifiedText;
    };

    return replaceAnswersWithCustomTags(activity.text);
  }, [activity.text]);

  const initOptions = useCallback(() => {
    const getPossibleAnswers = (text: string) => {
      const possibleAnswers: string[] = [];
      const re = /\[\[(.*?)\]\]/gi;
      let match;
      while ((match = re.exec(text))) {
        const item = unescape(match[1]);
        possibleAnswers.push(item);
      }
      return possibleAnswers;
    };

    setPossibleAnswers(getPossibleAnswers(activity.text));
  }, [activity, setPossibleAnswers]);

  useEffect(() => {
    initOptions();

    return () => {
      reset();
    };
  }, [activity, setPossibleAnswers, reset, initOptions]);

  useEffect(() => {
    if (!areErrorsVisible || complete) {
      return;
    }

    if (type === Type.DIB) {
      resetIncorrectAnswers();
    }

    const timeout = setTimeout(() => {
      if (type === Type.TIB) {
        resetIncorrectAnswers();
      }
      setErrorsVisible(false);
      setShowHintButton(true);
    }, 2000);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    areErrorsVisible,
    type,
    resetIncorrectAnswers,
    setErrorsVisible,
    complete,
  ]);

  const checkAnswers = useCallback(() => {
    if (areErrorsVisible) {
      return;
    }

    setErrorsVisible(true);

    if (areAllAnswersCorrect) {
      completeActivity();
      correctSound.play();
    } else {
      incorrectSound.play();
      vibrate();
    }
  }, [
    vibrate,
    areErrorsVisible,
    setErrorsVisible,
    completeActivity,
    correctSound,
    incorrectSound,
    areAllAnswersCorrect,
  ]);

  const handleHintPress = () => {
    if (hintButtonType === 'hint') {
      setHintButtonType('answer');
      fillAnswers(2);
    } else {
      fillAnswers(4);
    }
  };

  return (
    <KeyboardAvoidingView
      enabled
      style={styles.keyboardContainer}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={100}
    >
      <LessonPlayerFrame
        scrollY={scrollY}
        showCheckButton={hasAnsweredAll && !complete}
        onCheckButtonPress={checkAnswers}
        showContinueButton={complete}
      >
        <LessonAccentBanner>
          <LessonInstructions
            translation={currentActivity.instructionsTranslation}
          >
            {currentActivity.instructions}
          </LessonInstructions>
        </LessonAccentBanner>
        {!!currentActivity.audio && (
          <StickAudioPlayer
            scrollY={scrollY}
            containerStyle={styles.audioPlayer}
            url={currentActivity.audio}
            barContainerColor={colors.primaryOpacity}
            inactiveColor={colors.onPrimary}
            activeColor={colors.onPrimary}
            isTextProgressVisible
            isSlowButtonVisible
          />
        )}
        <LessonImage imageUrl={currentActivity?.image} style={styles.image} />
        <ThemedView style={styles.content} type="background">
          {type === Type.TIB && (
            <View style={styles.wordsContainer}>
              {possibleAnswers.map((option) => (
                <View key={option} style={styles.word}>
                  <Icon
                    color={colors.borderColor}
                    size={6}
                    name="circle"
                    type="solid"
                  />
                  <ThemedText2 style={styles.wordText}>{option}</ThemedText2>
                </View>
              ))}
            </View>
          )}

          <Card containerStyle={styles.card}>
            <HTML
              source={{ html: text }}
              baseFontStyle={{ ...HTMLFontStyle, color: colors.gray }}
              renderers={HTMLRenderers(type === Type.TIB ? 'text' : 'select')}
              tagsStyles={HTMLTagStyles}
              alterChildren={HTMLAlterChildren}
              defaultTextProps={{ selectable: true }}
            />
            {showHintButton && !complete && (
              <HintButton
                type={hintButtonType}
                colorType="gray"
                onPress={handleHintPress}
                containerStyle={styles.hintButton}
              />
            )}
          </Card>
        </ThemedView>
        <KeyboardLegendPadding />
      </LessonPlayerFrame>
      <KeyboardLegend
        templateType={type === Type.DIB ? TemplateType.DIB : TemplateType.TIB}
        forceHide={hasAnsweredSomething || isCurrentActivityComplete}
        text={t('keyboardLegend.navigate')}
        indicatorList={['Tab']}
      />
    </KeyboardAvoidingView>
  );
}

export default () => (
  <DibStoreProvider>
    <DibActivity />
  </DibStoreProvider>
);
